import { useContext, useEffect, useState } from "react";
import { loadingActive, pageLoadingActive } from "../../../components/Background";
import { eqSet, UnderConstruction } from "../../../utils";
import { RxPlusCircled } from "react-icons/rx";
import { AccountContext } from "../../../contexts/AccountContexts";
import { CiEdit } from "react-icons/ci";
import {
    ExpandableBubbleSidebar,
    ticketCategoryConfigs,
} from "../../../components/TicketComponents";
import { ContentContext } from "../../../contexts/ContentContext";
import {
    requestAddCategoriesToQuickReply,
    requestDeleteQuickReply,
    requestEditQuickReply,
    requestQuickReplies,
    requestSubmitNewQuickReply,
} from "../../../requests/setup_requests";
import { PageHeader } from "../../../components/HeaderComponents";
import { useConfirm } from "../../../contexts/ModalContexts";
import { useTranslation } from "react-i18next";
import { PermissionContext } from "../../../contexts/PermissionContexts";
import { useNavigate } from "react-router-dom";
import { BiCategory } from "react-icons/bi";
import { darkAndLightThemeDefault } from "../../../components/ThemeComponents";


export const QuickResponsesSetup = () => {
    const { t } = useTranslation('settings')

    const [editInProgress, setEditInProgress] = useState(false);
    const [openNew, setOpenNew] = useState(false);
    const [quickResponses, setQuickResponses] = useState([]);
    const { categories, openErrorModal } = useContext(ContentContext);
    const { hasPermission } = useContext(PermissionContext)

    const navigator = useNavigate()

    const handleOpenNew = () => {
        setOpenNew(true);
    };

    useEffect(() => {

        if (!hasPermission("quick_response:view")) {
            navigator("/admin/dashboard")
            return
        }

        pageLoadingActive(true)
        requestQuickReplies({})
            .then((data) => {
                setQuickResponses(data.quick_replies);
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, []);

    useEffect(() => {
        if (openNew) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [openNew]);

    if (!hasPermission("quick_response:view")) {
        return <></>
    }

    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600  rounded-md px-4 py-3 gap-3 min-h-[500px]">
            <PageHeader pageTitle={t("Quick Replies Setup")} />
            <span>{t('Set up the quick replies and their categories.')}</span>

            {quickResponses.map((quickResponse, index) => (
                <QuickResponseElement
                    key={quickResponse.id}
                    props={quickResponse}
                    setQuickResponses={setQuickResponses}
                    editInProgress={editInProgress}
                    setEditInProgress={setEditInProgress}
                />
            ))}

            {!openNew ? (
                hasPermission("quick_response:create") &&
                <div className="flex w-full justify-center mt-4">
                    <button
                        className="btn w-full btn-md h-9 p-0 text-primary dark:text-blue-600 bg-white dark:bg-gray-700 hover:text-white hover:bg-primary  border-gray-500 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-500"
                        disabled={editInProgress}
                        onClick={handleOpenNew}
                    >
                        <RxPlusCircled size={30} />
                    </button>
                </div>
            ) : (
                <QuickResponseElementEdit
                    key={"$$$$$$$$$"}
                    props
                    setOpen={setOpenNew}
                    setElement={setQuickResponses}
                    edit={false}
                />
            )}
        </div>
    );
};

const QuickResponseElementEdit = ({
    props,
    setOpen,
    setElement,
    edit,
}) => {
    const { t } = useTranslation('settings')
    const { user } = useContext(AccountContext);
    const { openErrorModal } = useContext(ContentContext)
    const confirm = useConfirm()

    const [title, setTitle] = useState(props.title ? props.title : "");
    const [message, setMessage] = useState(props.message ? props.message : "");

    const handleSubmit = () => {
        loadingActive(true);
        if (!edit) {
            requestSubmitNewQuickReply({
                title: title,
                message: message,
            })
                .then((data) => {
                    setElement(data.quick_replies);
                    setOpen(false);
                    loadingActive(false);
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false);

                });
        } else {
            requestEditQuickReply({
                id: props.id,
                title: title,
                message: message,
            })
                .then((data) => {
                    setElement(data.quick_replies);
                    setOpen(false);
                    loadingActive(false);
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false);

                });
        }
    };

    const handleDelete = async () => {
        const isConfirmed = await confirm(t("Are you sure? The quick reply will be lost indefinitely."))
        if (isConfirmed) {

            loadingActive(true);
            requestDeleteQuickReply({ id: props.id })
                .then((data) => {
                    setOpen((prev) => false);
                    setElement(data.quick_replies);
                    loadingActive(false);
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const submitCondition =
        message === "" ||
        title == "" ||
        (title === props.title && message === props.message);

    return (
        <div className="grid grid-cols-2 w-full gap-3 items-center border p-1 py-2 rounded-lg">
            <div className="flex flex-col gap-2">
                <label className="form-control">
                    <input
                        type="text"
                        placeholder={t('Title')}
                        className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                        }}
                    />
                </label>
                <label className="form-control">
                    <textarea
                        placeholder={t('Message')}
                        className="textarea textarea-bordered textarea-md w-full h-40 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={message}
                        onChange={(e) => {
                            setMessage(e.target.value);
                        }}
                    />
                </label>
            </div>
            <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">
                <button
                    className="btn bg-green-600 hover:bg-green-500 border-none text-white"
                    onClick={handleSubmit}
                    disabled={submitCondition}
                >
                    {t('Submit')}
                </button>
                {edit && (
                    <button className="btn bg-red-600 hover:bg-red-500 border-none text-white" onClick={handleDelete}>
                        {t('Delete')}
                    </button>
                )}
                <button className={`btn ${darkAndLightThemeDefault}`} onClick={handleClose}>
                    {t('Cancel')}
                </button>
            </div>
        </div>
    );
};

const QuickResponseElement = ({
    props,
    setQuickResponses,
    editInProgress,
    setEditInProgress,
}) => {
    const { t } = useTranslation(['settings', 'common'])
    const { language } = useContext(AccountContext)
    const { categories, openErrorModal } = useContext(ContentContext);
    const { hasPermission } = useContext(PermissionContext)
    const [edit, setEdit] = useState(false);
    const [newElements, setNewElements] = useState(null);
    const [openAddCategory, setOpenAddCategory] = useState(false);
    const [includedCategories, setIncludedCategories] = useState(props.categories);

    const handleOpenEdit = () => {
        setEdit(true);
    };

    useEffect(() => {
        if (edit) {
            setEditInProgress(true);
        } else {
            setEditInProgress(false);
        }
    }, [edit]);

    useEffect(() => {
        if (newElements !== null) {
            setQuickResponses(newElements);
            setNewElements(null);
        }
    }, [newElements]);

    useEffect(() => {
        if (Object.keys(categories).length > 0) {
            setIncludedCategories(props.categories);
        }
    }, [categories]);

    const handleOpenAddCategory = () => {
        if (!editInProgress) {
            setOpenAddCategory(true);
            setEditInProgress(true);
        }
    };

    const handleAddCategory = (category) => {
        setIncludedCategories((prevCategories) => [...prevCategories, category]);
    };

    const handleRemoveCategory = (category) => {
        setIncludedCategories((prevCategories) => [
            ...prevCategories.filter((cat) => cat !== category),
        ]);
    };

    const handleSubmit = () => {
        requestAddCategoriesToQuickReply({
            categories: includedCategories,
            id: props.id,
        })
            .then((data) => {
                setNewElements(data.quick_replies);
                setEditInProgress(false);
                setOpenAddCategory(false);
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
              })
    };

    const handleCancel = () => {
        setOpenAddCategory(false);
        setEditInProgress(false);
        setIncludedCategories(props.categories);
    };

    const submitCondition = eqSet(
        new Set(props.categories),
        new Set(includedCategories)
    );

    const getCategoryConfig = (category) => {
        return {
            status: categories[category] ? (categories[category]?.locales?.[language] ? categories[category]?.locales[language] : categories[category].title) : "UNK",
            style: categories[category] ? categories[category].style : "bg-primary text-white",
            icon: ticketCategoryConfigs[category] ? ticketCategoryConfigs[category].icon : <BiCategory />
        }
    }

    return !edit ? (
        <div className="flex flex-col w-full">
            <div
                className={`grid grid-cols-3 w-full gap-5 items-center group hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600  p-1 py-2 border rounded-lg ${editInProgress && !openAddCategory ? "bg-gray-100 dark:bg-gray-600" : ""
                    }`}
            >
                <div className="flex flex-col gap-2">
                    <span className="font-semibold">{props.title}</span>
                    <span className="h-10 overflow-clip text-wrap whitespace-break-spaces hover:h-40 hover:overflow-auto max-h-max transition-all duration-200">
                        {props.message}
                    </span>
                </div>
                <div className="flex">
                    <div
                        className={`cursor-pointer mr-1 ${editInProgress && "hidden"}`}
                        onClick={handleOpenAddCategory}
                    >
                        {
                            hasPermission("quick_response:set_categories") &&
                            <ExpandableBubbleSidebar
                                config={ticketCategoryConfigs["Add new"]}
                            />
                        }
                    </div>
                    {Object.keys(categories).length > 0 &&
                        props.categories.map((category) => (
                            <ExpandableBubbleSidebar
                                key={category}
                                config={getCategoryConfig(category)}
                            />
                        ))}
                </div>
                {
                    hasPermission("quick_response:edit") &&
                    <button
                        className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                        disabled={editInProgress}
                        onClick={handleOpenEdit}
                    >
                        <CiEdit size={24} />
                    </button>
                }
            </div>
            {openAddCategory && (
                <div className="grid grid-cols-3 py-3 px-2 border rounded-lg select-none">
                    <div className="flex flex-col gap-1">
                        <div className="flex flex-col bg-violet-100 text-gray-800 max-w-max p-1 rounded-lg">
                            <span className="font-semibold">{t('Added Categories')}</span>
                            <span>{t('Click to remove')}</span>
                        </div>
                        {Object.keys(categories).length > 0 &&
                            includedCategories.map((category) => (
                                <div
                                    key={category.id}
                                    className="cursor-pointer select-none ltr:hover:translate-x-1 rtl:hover:-translate-x-1 transition-all duration-200 max-w-max"
                                    onClick={() => {
                                        handleRemoveCategory(category);
                                    }}
                                >
                                    <span
                                        className={`${categories[category].style} rounded-full px-[6px]`}
                                    >
                                        {categories[category]?.locales?.[language] ? categories[category]?.locales[language] : t(categories[category].title, { 'ns': 'common' })}
                                    </span>
                                </div>
                            ))}
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="flex flex-col bg-green-100 text-gray-800 max-w-max p-1 rounded-lg">
                            <span className="font-semibold">{t('Other Categories')}</span>
                            <span>{t('Click to add')}</span>
                        </div>
                        {Object.keys(categories).length > 0 &&
                            Object.keys(categories)
                                .filter((category) => !includedCategories.includes(category))
                                .map((category) => (
                                    <div
                                        key={category.id}
                                        className=" cursor-pointer ltr:hover:translate-x-1 rtl:hover:-translate-x-1 transition-all duration-200 max-w-max"
                                        onClick={() => {
                                            handleAddCategory(category);
                                        }}
                                    >
                                        <span
                                            className={`${categories[category].style} rounded-full px-[6px] `}
                                        >
                                            {categories[category]?.locales?.[language] ? categories[category]?.locales[language] : t(categories[category].title, { 'ns': 'common' })}
                                        </span>
                                    </div>
                                ))}
                    </div>
                    <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">
                        <button
                            className="btn bg-green-600 hover:bg-green-500 border-none text-white"
                            onClick={handleSubmit}
                            disabled={submitCondition}
                        >
                            {t('Submit')}
                        </button>

                        <button
                            className={`btn ${darkAndLightThemeDefault}`}
                            onClick={handleCancel}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    ) : (
        <QuickResponseElementEdit
            key={props.id}
            props={props}
            setOpen={setEdit}
            setElement={setNewElements}
            edit={true}
        />
    );
};
